package com.klantenstop.web.utils

import com.klantenstop.web.bindings.KlaroWatcher
import com.klantenstop.web.bindings.klaro
import com.materialkobweb.utils.Logger

const val POSTHOG_CONSENT_KEY = "posthog"
const val ADSENSE_CONSENT_KEY = "adsense"

/**
 * Central Klaro consent management utility.
 *
 * Manages all Klaro watchers and provides a way for different parts of the app
 * (analytics, marketing, etc.) to listen to consent changes for their respective services.
 *
 * Usage:
 *   KlaroConsentManager.onConsentChanged("posthog") { granted ->
 *       // React to PostHog consent change
 *   }
 */
object KlaroConsentManager {

    private var initialized: Boolean = false

    /**
     * Map of service name -> list of callback functions to invoke on consent change.
     */
    private val consentCallbacks = mutableMapOf<String, MutableList<(Boolean) -> Unit>>()

    /**
     * Initialize Klaro consent manager.
     * Should be called once at app startup.
     */
    fun initialize() {
        if (initialized) return

        initialized = true

        try {
            val manager = klaro.getManager()

            // Create and register a master watcher for all consent changes
            val masterWatcher = object : KlaroWatcher {
                override fun update(obj: dynamic, name: String, data: dynamic) {
                    if (obj.confirmed != true || name != "saveConsents" || data == null) return

                    // Notify all registered callbacks for each service
                    consentCallbacks.forEach { (serviceName, callbacks) ->
                        val isGranted = data.consents[serviceName] as? Boolean ?: false
                        callbacks.forEach { callback ->
                            try {
                                callback(isGranted)
                            } catch (e: Throwable) {
                                Logger.error("KlaroManager", "Callback error for $serviceName:", e)
                            }
                        }
                    }
                }
            }

            manager.watch(masterWatcher)
            Logger.info("KlaroManager", "Klaro consent manager initialized")
        } catch (e: Throwable) {
            Logger.warn("KlaroManager", "Failed to initialize Klaro:", e)
        }
    }

    /**
     * Open the Klaro consent settings modal directly.
     * Use this when the user explicitly wants to manage their consent preferences,
     * e.g. from a Settings page button.
     */
    fun showConsentModal() {
        try {
            klaro.show(js("undefined"), true)
        } catch (e: Throwable) {
            Logger.warn("KlaroManager", "Failed to show consent modal:", e)
        }
    }

    /**
     * Get the current consent state for a specific service.
     *
     * @param serviceName  The service name (e.g. "posthog", "marketing", "functional")
     * @return  True if consent is granted, false otherwise
     */
    fun getConsent(serviceName: String): Boolean {
        return try {
            val manager = klaro.getManager()
            manager.getConsent(serviceName) as? Boolean == true
        } catch (e: Throwable) {
            Logger.warn("KlaroManager", "Could not get consent for $serviceName:", e)
            false
        }
    }

    /**
     * Register a callback to be invoked whenever consent changes for a specific service.
     *
     * @param serviceName  The service name to listen to (e.g. "posthog")
     * @param callback     Function to invoke with the new consent state (granted: Boolean)
     */
    fun onConsentChanged(serviceName: String, callback: (Boolean) -> Unit) {
        if (!consentCallbacks.containsKey(serviceName)) {
            consentCallbacks[serviceName] = mutableListOf()
        }
        consentCallbacks[serviceName]?.add(callback)
        Logger.info("KlaroManager", "Registered consent callback for $serviceName")

        // Immediately invoke with current state so caller always has initial value
        callback(getConsent(serviceName))
    }
}


